<template>
    <div>
      <AdminHeader />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Business List</h5>
          <p class="page-sub">Manage all your businesses and companies</p>
        </div>
        <div class="page-actions">
  
          <!-- Search -->
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input type="text" class="search-input" placeholder="Search businesses..." v-model="query" />
          </div>
  
          <!-- Export -->
          <div class="dropdown">
            <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <font-awesome-icon icon="download" /> Export
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li><a class="dropdown-item" href="#" @click="drawPdf(print_content, headerArrayColumn, 'p', 'Businesses', 'Business')">
                <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> PDF
              </a></li>
              <li><a class="dropdown-item" href="#" @click="reusableMixinToCSV('businesses.csv')">
                <font-awesome-icon icon="file-csv" class="me-2 text-success" /> CSV
              </a></li>
            </ul>
          </div>
  
          <!-- Create -->
          <router-link
            to="create-business"
            class="btn-primary-action"
            :class="permission.create_company != 1 ? 'disabled' : ''"
          >
            <font-awesome-icon icon="plus" /> Create Business
          </router-link>
  
          <!-- Bulk Delete -->
          <button
            class="btn-danger-action"
            data-bs-toggle="modal"
            data-bs-target="#bulkModal"
            :disabled="permission.delete_company != 1 || selectedModel.length === 0"
          >
            <font-awesome-icon icon="trash" /> Bulk Delete
            <span v-if="selectedModel.length > 0" class="badge-count">{{ selectedModel.length }}</span>
          </button>
  
        </div>
      </div>
  
      <!-- Data Card -->
      <div class="data-card">
  
        <!-- Loading -->
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted">Loading businesses...</p>
        </div>
  
        <!-- No Permission -->
        <div v-else-if="permission_status == 403" class="state-block">
          <div class="state-icon state-icon--red"><font-awesome-icon icon="lock" /></div>
          <p class="state-title">Access Denied</p>
          <p class="state-sub">You don't have permission to access this resource.</p>
        </div>
  
        <!-- Empty -->
        <div v-else-if="details.length === 0" class="state-block">
          <div class="state-icon state-icon--blue"><font-awesome-icon icon="building" /></div>
          <p class="state-title">No businesses yet</p>
          <p class="state-sub">Get started by creating your first business.</p>
          <router-link v-if="permission.create_company == 1" to="create-business" class="btn-primary-action mt-2">
            <font-awesome-icon icon="plus" /> Create Business
          </router-link>
        </div>
  
        <!-- Table -->
        <template v-else>
  
          <div class="table-toolbar">
            <span class="table-count">
              Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong> businesses
            </span>
            <div class="d-flex align-items-center gap-2">
              <label class="table-count">Rows:</label>
              <select v-model.number="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
                <option v-for="n in [5,10,20,50,100,200,500,1000]" :key="n" :value="n">{{ n }}</option>
              </select>
            </div>
          </div>
  
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th class="col-check">
                    <input type="checkbox" v-model="allSelected" @change="selectAll" class="form-check-input" />
                  </th>
                  <th>Business</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Address</th>
                  <th>Country Code</th>
                  <th class="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="company in details" :key="company.id" :class="selectedModel.includes(company.id) ? 'row-selected' : ''">
                  <td class="col-check">
                    <input type="checkbox" v-model="selectedModel" :value="company.id" class="form-check-input" />
                  </td>
                  <td>
                    <div class="business-cell">
                      <div class="business-logo" v-if="company.logo">
                        <img :src="host.hostName + company.logo" :alt="company.name" />
                      </div>
                      <div class="business-logo business-logo--placeholder" v-else>
                        <font-awesome-icon icon="building" />
                      </div>
                      <span class="business-name">{{ company.name }}</span>
                    </div>
                  </td>
                  <td class="text-muted-cell">
                    <div>{{ company.email }}</div>
                    <div v-if="company.email2" class="text-xs">{{ company.email2 }}</div>
                  </td>
                  <td class="text-muted-cell">
                    <div>{{ company.phone_number }}</div>
                    <div v-if="company.phone_number2" class="text-xs">{{ company.phone_number2 }}</div>
                    <div v-if="company.phone_number3" class="text-xs">{{ company.phone_number3 }}</div>
                  </td>
                  <td class="text-muted-cell">{{ company.address }}</td>
                  <td class="text-muted-cell">{{ company.country_code }}</td>
                  <td class="col-actions">
                    <div class="action-btns">
                      <router-link
                        :to="permission.update_company == 1 ? { name: 'Edit Business', params: { companyCode: $route.params.companyCode, id: company.id } } : '#'"
                        class="action-btn action-btn--edit"
                        :class="permission.update_company != 1 ? 'disabled' : ''"
                        title="Edit business"
                      >
                        <font-awesome-icon icon="pen" />
                      </router-link>
                      <button
                        class="action-btn action-btn--delete"
                        :data-bs-toggle="permission.delete_company == 1 ? 'modal' : ''"
                        data-bs-target="#singleModal"
                        @click="getOneModal(company)"
                        :disabled="permission.delete_company != 1"
                        title="Delete business"
                      >
                        <font-awesome-icon icon="trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
  
          <div class="table-footer">
            <pagination
              :pagination="pagination"
              @prev="tableData.page = pagination.currentPage - 1; fetchData()"
              @next="tableData.page = pagination.currentPage + 1; fetchData()"
            />
          </div>
  
        </template>
      </div>
  
      <SingleDeleteModal :modalId="'singleModal'" @deleteSingle="callSingleCompanyDeleteButton" :modalText="'Are you sure you want to delete this company?'" :action="'deleteSingle'" />
      <BulkDeleteModal :modalId="'bulkModal'" @deleteBulk="callBulkCompanyDeleteButton" :modalText="'Are you sure you want to delete the selected companies?'" />
  
    </div>
  </template>
  
  <script>
  import host from "../../../main";
  import AdminHeader from "./administrationheadertab.vue";
  import SingleDeleteModal from '../../../components/ActionModal.vue';
  import BulkDeleteModal from '../../../components/bulkDeleteModal.vue';
  import { reusableMixin } from "../../../lib/reusableMixin";
  import "jspdf-autotable";
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { useStore } from 'vuex';
  
  export default {
    components: { AdminHeader, SingleDeleteModal, BulkDeleteModal },
    mixins: [reusableMixin],
  
    data() {
      return {
        print_content: [],
        headerArrayColumn: ['S/N', 'Business Name', 'Email', 'Phone Number', 'Address'],
        details: [], loading: false, query: null, company_id: null,
        selectedModel: [], allSelected: false,
        tableData: { page: 1, draw: 0, length: 5, search: '', column: 0, dir: 'desc', id: '', status: [0,1,2,3], query: '' },
        pagination: { lastPage:'', currentPage:'', total:'', lastPageUrl:'', nextPageUrl:'', prevPageUrl:'', from:'', to:'' },
        toast: useToast(), permission_status: null, store: useStore(), host,
      };
    },
  
    computed: {
      permission() { return this.store.state.getCurrentUserPermission; }
    },
  
    watch: { query() { this.fetchData(); } },
    created() { this.fetchData(); },
  
    methods: {
      dataToPrint(details) {
        this.print_content = details.map((c, idx) => ({
          sn: idx + 1, business_name: c.name, email: c.email,
          phone_number: c.phone_number, address: c.address,
        }));
      },
  
      getOneModal(company) { this.company_id = company.id; },
  
      selectAll() {
        this.selectedModel = this.allSelected ? this.details.map(u => u.id) : [];
      },
  
      fetchData(url = '/admin/business/') {
        this.loading = true;
        this.tableData.query = this.query;
        this.tableData.draw++;
        axios.get(url, { params: this.tableData })
          .then(response => {
            const data = response.data;
            this.details = data.data.businesses.data;
            this.dataToPrint(data.data.businesses2);
            this.configPagination(data.data.businesses);
            this.loading = false;
          })
          .catch(error => {
            this.loading = false;
            if (error?.response?.status == 403) {
              this.permission_status = 403;
              this.toast.error(error.response.data.message);
            }
          });
      },
  
      configPagination(data) {
        this.pagination.lastPage    = data.last_page;
        this.pagination.currentPage = data.current_page;
        this.pagination.total       = data.total;
        this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url;
        this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from        = data.from;
        this.pagination.to          = data.to;
      },
  
      callSingleCompanyDeleteButton() {
        axios.delete(`/admin/business/${this.company_id}`)
          .then(() => { this.toast.success('Company deleted successfully'); this.fetchData(); })
          .catch(error => { this.toast.error(error?.response?.data?.message ?? 'Something went wrong'); });
      },
  
      callBulkCompanyDeleteButton() {
        axios.post('/admin/business/delete-bulk', { companies: this.selectedModel })
          .then(response => { this.toast.success(response.data.message); this.fetchData(); })
          .catch(error => { this.toast.error(error?.response?.data?.message ?? 'Something went wrong'); });
      },
    },
  };
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub    { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  
  /* ── Search ── */
  .search-wrap  { position:relative; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 32px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:220px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  .search-input::placeholder { color:#aab8cc; }
  
  /* ── Buttons ── */
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; text-decoration:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-primary-action:hover { background:#003b8e; color:#fff; text-decoration:none; }
  .btn-primary-action.disabled { opacity:.5; pointer-events:none; }
  
  .btn-danger-action { display:inline-flex; align-items:center; gap:7px; background:#fff0f0; color:#e03535; border:1px solid #fac5c5; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-danger-action:hover:not(:disabled) { background:#ffe0e0; }
  .btn-danger-action:disabled { opacity:.45; cursor:not-allowed; }
  
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-outline:hover { background:#f4f7fb; }
  .btn-outline::after { display:none !important; }
  
  .badge-count { display:inline-flex; align-items:center; justify-content:center; background:#e03535; color:#fff; font-size:10px; font-weight:700; width:18px; height:18px; border-radius:50%; }
  
  /* ── Data Card ── */
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  /* ── States ── */
  .state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon  { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--blue { background:rgba(0,74,173,.1); color:#004AAD; }
  .state-icon--red  { background:rgba(224,53,53,.1); color:#e03535; }
  .state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub   { font-size:13px; color:#7a90b0; margin:0; }
  
  /* ── Toolbar ── */
  .table-toolbar { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .table-count   { font-size:13px; color:#7a90b0; }
  .rows-select   { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }
  
  /* ── Table ── */
  .table-wrap { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr  { background:#f8fafc; }
  .data-table thead th  { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr  { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover  { background:#f8fafc; }
  .data-table tbody tr.row-selected { background:#edf4ff; }
  .data-table tbody td  { padding:13px 16px; font-size:13.5px; vertical-align:middle; }
  
  .col-check   { width:44px; text-align:center; }
  .col-actions { width:90px; text-align:center; }
  .text-muted-cell { color:#4a5e78; font-size:13px; }
  .text-xs     { font-size:11.5px; color:#7a90b0; margin-top:2px; }
  
  /* ── Business Cell ── */
  .business-cell { display:flex; align-items:center; gap:10px; }
  .business-logo { width:38px; height:38px; border-radius:8px; overflow:hidden; flex-shrink:0; }
  .business-logo img { width:100%; height:100%; object-fit:contain; }
  .business-logo--placeholder { background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:15px; }
  .business-name { font-weight:600; color:#1a2b45; }
  
  /* ── Action Buttons ── */
  .action-btns { display:flex; align-items:center; justify-content:center; gap:6px; }
  .action-btn  { width:32px; height:32px; border-radius:7px; border:1px solid #e4eaf2; display:flex; align-items:center; justify-content:center; font-size:13px; cursor:pointer; transition:background .15s,border-color .15s; text-decoration:none; background:#fff; }
  .action-btn--edit { color:#004AAD; }
  .action-btn--edit:hover:not(.disabled) { background:#edf4ff; border-color:#c5d6f5; }
  .action-btn--delete { color:#e03535; }
  .action-btn--delete:hover:not(:disabled) { background:#fff0f0; border-color:#fac5c5; }
  .action-btn--delete:disabled { color:#ccc; cursor:not-allowed; border-color:#f0f0f0; }
  .action-btn.disabled { color:#ccc; pointer-events:none; }
  
  /* ── Footer ── */
  .table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }
  
  /* ── Dropdown ── */
  .dropdown-menu { border:1px solid #e4eaf2; border-radius:9px; box-shadow:0 8px 24px rgba(0,0,0,.08); padding:6px; min-width:170px; }
  .dropdown-item { border-radius:6px; font-size:13px; padding:8px 12px; color:#1a2b45; }
  .dropdown-item:hover { background:#f4f7fb; }
  
  /* ── Responsive ── */
  @media(max-width:767px) {
    .page-header  { flex-direction:column; align-items:stretch; }
    .page-actions { flex-direction:column; }
    .search-input { width:100%; }
    .btn-primary-action,.btn-danger-action,.btn-outline { width:100%; justify-content:center; }
  }
  </style>